/**
 * @file userService.js
 * @description APIs for User related requests.
 **/

import { clientApiHttp, publicClientApiHttp } from './httpService';

/**
 *
 * @returns {} - A promise that resolve to the user onboarding details
 */

/*
 * this table serves two purposes. One is to translate between naming conventions,
 * which would be possible in a more general way, and the other is to restrict the
 * usage of this service to a set of valid types. As org levels don't have an API namespace,
 * using this service with the wrong type parameter might produce unexpected results
 */

/**
 * Retrieve the tenant ID from local storage.
 * @returns {string|null} The tenant ID or null if not found.
 */
const getTenantId = () => +window.localStorage.getItem('tenantId');

// remove the last character if it's an 's' to translate from plural to singular for the backend
const calcOrgLevelTypeToURL = orgLevelType => {
    return orgLevelType.endsWith('s') ? orgLevelType.slice(0, -1) : orgLevelType;
}

export const getOrgLevelsForType = async orgLevelType => {
    let tenantID = getTenantId();
    try {
        const response = await clientApiHttp.get(
            `/v2/client/${tenantID}/${calcOrgLevelTypeToURL(orgLevelType)}`,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error fetching current user data:', error);
        throw error;
    }
};

export const updateOrgLevelForTypeAndID = async (
    orgLevelType,
    orgLevelID,
    payload,
) => {
    let tenantID = getTenantId();
    try {
        const response = await clientApiHttp.put(
            `/v2/client/${tenantID}/${calcOrgLevelTypeToURL(orgLevelType)}`,
            payload,
        );
        return response;
    } catch (error) {
        console.error('Error fetching current user data:', error);
        return {
            error: true,
            message: error.response?.data?.detail,
            status: error.response?.status || 500,
        };
    }
};

export const deleteOrgLevelForTypeAndID = async (orgLevelType, orgLevelID) => {
    let tenantID = getTenantId();
    try {
        const response = await clientApiHttp.delete(
            `/v2/client/${tenantID}/${calcOrgLevelTypeToURL(orgLevelType)}/${orgLevelID}`,
        );
        return response;
    } catch (error) {
        console.error('Error fetching current user data:', error);
        return {
            error: true,
            message: error.response?.data?.detail,
            status: error.response?.status || 500,
        };
    }
};

export const createOrgLevelForType = async (orgLevelType, payload) => {
    let tenantID = getTenantId();
    try {
        const response = await clientApiHttp.post(
            `/v2/client/${tenantID}/${calcOrgLevelTypeToURL(orgLevelType)}`,
            payload,
        );
        return response;
    } catch (error) {
        console.error('Error fetching current user data:', error);
        return {
            error: true,
            message: error.response?.data?.detail,
            status: error.response?.status || 500,
        };
    }
};

/**
 * Fetches the business units for a given tenant.
 * If no tenantId is provided, it will use the default tenantId.
 *
 * @param {string} tenantId - The ID of the tenant.
 * @returns {Promise<Array>} - A promise that resolves to an array of business units.
 * @throws {Error} - If there is an error fetching the business units.
 */
export const fetchBusinessUnits = async (
    tenantId,
    { isPublic = false, companyCode = null } = {},
) => {
    try {
        if (isPublic) {
            const response = await publicClientApiHttp.get(
                `/v2/client/business-unit?companyCode=${companyCode}`,
            );
            return response.data;
        }
        if (!tenantId) tenantId = getTenantId();
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/business-unit`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching business units:', error);
        throw error;
    }
};

/**
 * Fetches the pay groups for a given tenant.
 * If no tenantId is provided, it will use the default tenantId.
 *
 * @param {string} tenantId - The ID of the tenant.
 * @returns {Promise<Array>} - A promise that resolves to an array of pay groups.
 * @throws {Error} - If there is an error fetching the pay groups.
 */
export const fetchPayGroups = async (
    tenantId,
    { isPublic = false, companyCode = null } = {},
) => {
    try {
        if (isPublic) {
            const response = await publicClientApiHttp.get(
                `/v2/client/pay-group?companyCode=${companyCode}`,
            );
            return response.data;
        }
        if (!tenantId) tenantId = getTenantId();
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/pay-group`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching Pay Groups:', error);
        throw error;
    }
};

/**
 * Fetches divisions for a given tenant.
 * @param {string} tenantId - The ID of the tenant.
 * @returns {Promise<Array>} - A promise that resolves to an array of divisions.
 * @throws {Error} - If there is an error fetching divisions.
 */
export const fetchDivisions = async (
    tenantId,
    { isPublic = false, companyCode = null } = {},
) => {
    try {
        if (isPublic) {
            const response = await publicClientApiHttp.get(
                `/v2/client/division?companyCode=${companyCode}`,
            );
            return response.data;
        }
        if (!tenantId) tenantId = getTenantId();
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/division`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching Divisions:', error);
        throw error;
    }
};

/**
 * Fetches the departments for a given tenant.
 * If tenantId is not provided, it retrieves the tenantId from getTenantId() function.
 *
 * @param {string} tenantId - The ID of the tenant.
 * @returns {Promise<Array>} - A promise that resolves to an array of departments.
 * @throws {Error} - If there is an error fetching the departments.
 */
export const fetchDepartments = async (
    tenantId,
    { isPublic = false, companyCode = null } = {},
) => {
    try {
        if (isPublic) {
            const response = await publicClientApiHttp.get(
                `/v2/client/department?companyCode=${companyCode}`,
            );
            return response.data;
        }
        if (!tenantId) tenantId = getTenantId();
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/department`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching departments:', error);
        throw error;
    }
};

/**
 * Fetches locations for a given tenant ID.
 * If tenant ID is not provided, it will use the default tenant ID.
 *
 * @param {string} tenantId - The ID of the tenant.
 * @returns {Promise<Array>} - A promise that resolves to an array of locations.
 * @throws {Error} - If there is an error fetching the locations.
 */
export const fetchLocations = async (
    tenantId,
    { isPublic = false, companyCode = null } = {},
) => {
    try {
        if (isPublic) {
            const response = await publicClientApiHttp.get(
                `/v2/client/location?companyCode=${companyCode}`,
            );
            return response.data;
        }
        if (!tenantId) tenantId = getTenantId();
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/location`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching locations:', error);
        throw error;
    }
};

/**
 * Fetches regions for a given tenant.
 * @param {string} tenantId - The ID of the tenant.
 * @returns {Promise<Array>} - A promise that resolves to an array of regions.
 * @throws {Error} - If there is an error fetching the regions.
 */
export const fetchRegions = async (
    tenantId,
    { isPublic = false, companyCode = null } = {},
) => {
    try {
        if (isPublic) {
            const response = await publicClientApiHttp.get(
                `/v2/client/region?companyCode=${companyCode}`,
            );
            return response.data;
        }
        if (!tenantId) tenantId = getTenantId();
        const response = await clientApiHttp.get(
            `/v2/client/${tenantId}/region`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching regions:', error);
        throw error;
    }
};
